import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Button,
  IconButton,
  Flex,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { RiFilePdfFill } from "react-icons/ri";
import { crm } from "../../../theme";
import { saveItems, importItemsAI } from "../../../services/productionService";
import {
  ProductionOrderItemCommand,
  ProductionOrderViewModel,
} from "../../../models/Production";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  order: ProductionOrderViewModel;
  onSaved: () => void;
}

const ProductsModal = ({ isOpen, onClose, order, onSaved }: Props) => {
  const [items, setItems] = useState<ProductionOrderItemCommand[]>([]);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setItems(
        (order.items ?? []).map((i) => ({
          order: i.order,
          ncm: i.ncm,
          quantity: i.quantity,
          product: i.product,
          unitValue: i.unitValue,
        })),
      );
    }
  }, [isOpen, order]);

  const handleChange = (index: number, field: keyof ProductionOrderItemCommand, value: string) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const handleAdd = () => {
    setItems((prev) => [...prev, { order: prev.length }]);
  };

  const handleRemove = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!order.id) return;
    setSaving(true);
    await saveItems(order.id, items);
    setSaving(false);
    onSaved();
    onClose();
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !order.id) return;
    setImporting(true);
    const result = await importItemsAI(file, order.id);
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!result?.success) {
      toast({ title: "Falha ao importar itens do PDF", status: "error" });
      return;
    }
    onSaved();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Produtos {order.processCvfRef ? `— Processo ${order.processCvfRef}` : ""}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex justify="flex-end" mb="16px">
            <Button
              variant="outline"
              borderColor={crm.border}
              color={crm.text}
              onClick={handleImportClick}
              isLoading={importing}
            >
              <Flex align="center" gap="8px">
                <RiFilePdfFill /> Importar por IA
              </Flex>
            </Button>
            <input
              type="file"
              accept="application/pdf"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Flex>

          <Table size="sm">
            <Thead>
              <Tr>
                <Th>NCM</Th>
                <Th>Quantidade</Th>
                <Th>Produto</Th>
                <Th>Valor Unitário</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((item, index) => (
                <Tr key={index}>
                  <Td>
                    <Input
                      size="sm"
                      value={item.ncm ?? ""}
                      onChange={(e) => handleChange(index, "ncm", e.target.value)}
                    />
                  </Td>
                  <Td>
                    <Input
                      size="sm"
                      value={item.quantity ?? ""}
                      onChange={(e) => handleChange(index, "quantity", e.target.value)}
                    />
                  </Td>
                  <Td>
                    <Input
                      size="sm"
                      value={item.product ?? ""}
                      onChange={(e) => handleChange(index, "product", e.target.value)}
                    />
                  </Td>
                  <Td>
                    <Input
                      size="sm"
                      value={item.unitValue ?? ""}
                      onChange={(e) => handleChange(index, "unitValue", e.target.value)}
                    />
                  </Td>
                  <Td>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      color="red"
                      aria-label="Remover"
                      icon={<FaTrash />}
                      onClick={() => handleRemove(index)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {items.length === 0 && (
            <Text color={crm.textMuted} textAlign="center" mt="16px">
              Nenhum item cadastrado
            </Text>
          )}

          <Button mt="16px" variant="ghost" color={crm.brand} onClick={handleAdd}>
            <Flex align="center" gap="8px">
              <FaPlus /> Adicionar item
            </Flex>
          </Button>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="12px" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            backgroundColor={crm.brand}
            color="white"
            _hover={{ backgroundColor: crm.brandDark }}
            onClick={handleSave}
            isLoading={saving}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProductsModal;
